import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  VStack,
  Flex,
  Button,
  Divider,
  Icon,
  Text,
  IconButton,
  Image,
  HStack,
  Collapse,
  Menu,
  MenuButton,
  MenuList,
  MenuItem as ChakraMenuItem,
  MenuDivider,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import {
  EmailIcon,
  SettingsIcon,
  QuestionIcon,
  ArrowBackIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InfoIcon,
  ChatIcon,
} from '@chakra-ui/icons';
import {
  FaUsers, 
  FaUserFriends, 
  FaHistory, 
  FaEnvelope,
  FaUserCog,
  FaAddressBook,
  FaToolbox,
  FaFileAlt,
  FaEnvelopeOpen,
  FaHome,
  FaUser,
  FaUserShield,
  FaIdCard,
  FaSignOutAlt,
  FaUserPlus,
} from 'react-icons/fa';
import { FiShield } from 'react-icons/fi';
import { BsCalendar } from 'react-icons/bs';
import { useSupabaseClient, useUser } from '@supabase/auth-helpers-react';
import RotaryLogo from './RotaryLogo';
import ProductName from './ProductName';
import { useDisclosure } from '@chakra-ui/react';
import HelpDialog from './HelpDialog';
import { version } from '../../package.json';
import { useMenu } from '../hooks/useMenu';
import { ICON_MAP } from '../utils/iconMap';

const DRAWER_WIDTH = "320px";

const MenuGroup = ({ title, children, icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  const IconComponent = typeof icon === 'string' ? ICON_MAP[icon] : icon;

  return (
    <Box>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="ghost"
        width="100%"
        justifyContent="flex-start"
        leftIcon={IconComponent ? <Icon as={IconComponent} /> : undefined}
        rightIcon={<ChevronDownIcon transform={isOpen ? 'rotate(180deg)' : undefined} />}
        mb={1}
      >
        {title}
      </Button>
      <Collapse in={isOpen}>
        <VStack spacing={0} pl={4} pt={1} pb={1} align="stretch">
          {children}
        </VStack>
      </Collapse>
    </Box>
  );
};

const MenuItem = ({ icon, text, path, isSubmenu = false, onClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const IconComponent = typeof icon === 'string' ? ICON_MAP[icon] : icon;

  const isActive = location.pathname === path;

  const handleClick = () => {
    console.log('MenuItem clicked:', { text, path });
    if (onClick) {
      onClick();
    } else if (path) {
      console.log('Navigating to:', path);
      navigate(path);
    }
  };

  return (
    <Button
      leftIcon={<Icon as={IconComponent} />}
      variant="ghost"
      justifyContent="flex-start"
      w="calc(100% - 4px)"
      h={isSubmenu ? "32px" : "40px"}
      fontSize={isSubmenu ? "sm" : "md"}
      fontWeight="normal"
      pl={isSubmenu ? 8 : 4}
      pr={3}
      mx="2px"
      color={isActive ? "blue.500" : "gray.800"}
      bg={isActive ? "blue.50" : "transparent"}
      _hover={{ bg: isActive ? "blue.100" : "gray.100" }}
      onClick={handleClick}
      mb={isSubmenu ? "1px" : "2px"}
    >
      {text}
    </Button>
  );
};

const Sidebar = () => {
  const navigate = useNavigate();
  const supabase = useSupabaseClient();
  const user = useUser();
  const [showHelp, setShowHelp] = useState(false);
  const { menuStructure, loading, error } = useMenu();

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <Box
      as="nav"
      pos="fixed"
      h="100vh"
      borderRight="1px"
      borderRightColor="gray.200"
      w={DRAWER_WIDTH}
      bg="white"
      display="flex"
      flexDirection="column"
    >
      {/* Logo and Title Section */}
      <Box p={4} borderBottom="1px" borderBottomColor="gray.200">
        <VStack spacing={3} align="center">
          <Box width="100%" display="flex" justifyContent="center">
            <RotaryLogo containerWidth={parseInt(DRAWER_WIDTH)} />
          </Box>
          <ProductName />
        </VStack>
      </Box>

      {/* Menu Section */}
      <Box
        flex="1"
        overflowY="auto"
        pr="4px"
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '24px',
          },
        }}
      >
        <VStack p={4} pr={2} spacing={0} align="stretch">
          {loading ? (
            <Flex justify="center" align="center" h="100px">
              <Spinner />
            </Flex>
          ) : error ? (
            <Text color="red.500" p={4}>Error loading menu</Text>
          ) : (
            <VStack spacing={0} align="stretch" w="100%">
              {menuStructure.map((group) => (
                <MenuGroup
                  key={group.id}
                  title={group.name}
                  icon={group.icon}
                >
                  {group.items.map((item) => (
                    <MenuItem
                      key={item.id}
                      icon={item.icon}
                      text={item.name}
                      path={item.path}
                      isSubmenu
                    />
                  ))}
                </MenuGroup>
              ))}
            </VStack>
          )}
        </VStack>
      </Box>

      {/* Bottom Section */}
      <Box p={4} borderTop="1px" borderTopColor="gray.200">
        <VStack spacing={1} align="stretch" w="100%">
          <Box width="160px">
            <Menu placement="top-start">
              <MenuButton
                as={Button}
                leftIcon={<Icon as={QuestionIcon} boxSize={4} />}
                rightIcon={<ChevronUpIcon boxSize={4} />}
                variant="ghost"
                size="sm"
                w="auto"
                minW="fit-content"
                h="40px"
                fontSize="md"
                fontWeight="bold"
                color="gray.800"
                _hover={{ bg: "gray.100" }}
                px={3}
              >
                Help
              </MenuButton>
              <MenuList minW="auto" p={1} boxShadow="md">
                <ChakraMenuItem 
                  icon={<InfoIcon boxSize={4} />}
                  onClick={() => setShowHelp(true)}
                  _hover={{ bg: "gray.100" }}
                  fontSize="sm"
                >
                  How to Use RoText
                </ChakraMenuItem>
                <ChakraMenuItem 
                  icon={<Icon as={FaFileAlt} boxSize={4} />}
                  onClick={() => navigate('/public-releases')}
                  _hover={{ bg: "gray.100" }}
                  fontSize="sm"
                >
                  View Releases
                </ChakraMenuItem>
                <ChakraMenuItem 
                  icon={<ChatIcon boxSize={4} />}
                  isDisabled
                  _hover={{ bg: "gray.100" }}
                  fontSize="sm"
                >
                  <HStack justify="space-between" width="100%">
                    <Text>Chat Assistant</Text>
                    <Text fontSize="xs" color="gray.500">(Coming Soon)</Text>
                  </HStack>
                </ChakraMenuItem>
              </MenuList>
            </Menu>
          </Box>
          <Button
            leftIcon={<Icon as={FaSignOutAlt} />}
            variant="ghost"
            justifyContent="flex-start"
            w="100%"
            h="40px"
            fontSize="md"
            fontWeight="bold"
            color="gray.800"
            onClick={handleSignOut}
            _hover={{ bg: "gray.100" }}
          >
            Sign Out
          </Button>
        </VStack>
      </Box>

      {/* Help Dialog */}
      <HelpDialog isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </Box>
  );
};

export default Sidebar;